import React from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import useSubscriptions from '@afosto/utils-react/subscription/useSubscriptions';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import Attach from '../pages/Attach';
import CalculationInvoice from '../pages/CalculationInvoice';
import CalculationView from '../pages/CalculationView';
import CreateCalculation from '../pages/CreateCalculation';
import CreateInvoice from '../pages/CreateInvoice';
import CreateOrder from '../pages/CreateOrder';
import CreatePricingRule from '../pages/CreatePricingRule';
import EditOrder from '../pages/EditOrder';
import EditPricingRule from '../pages/EditPricingRule';
import Invoices from '../pages/Invoices';
import InvoiceView from '../pages/InvoiceView';
import OrderLines from '../pages/OrderLines';
import Orders from '../pages/Orders';
import OrderView from '../pages/OrderView';
import PricingRules from '../pages/PricingRules';
import Providers from '../pages/Providers';
import Transactions from '../pages/Transactions';
import TransactionView from '../pages/TransactionView';
import AppLayout from '@afosto/components-react/AppLayout';
import AppWorkspace from '@afosto/components-react/AppWorkspace';
import Dock from '@afosto/app-components-react/Dock';
import Drawer from '@afosto/app-components-react/Drawer';
import DrawerMenu from '@afosto/app-components-react/DrawerMenu';
import { menu, wmsItemsMenu } from '../data/navigationMenu';
import translations from './translations';
import ServiceCosts from '../pages/ServiceCosts';
import CreateServiceCost from '../pages/CreateServiceCost';
import EditServiceCost from '../pages/EditServiceCost';
import CouponCategories from '../pages/CouponCategories';
import CouponCategoryView from '../pages/CouponCategoryView';

const Root = () => {
  const intl = useIntl();
  const [hasPermissions] = usePermissions();
  const history = useHistory();
  const subscriptions = useSubscriptions();
  const hasWmsSubscription = subscriptions.some(subscription => subscription.code === 'wms');
  const navigationMenu = [...menu];

  if (hasWmsSubscription) {
    navigationMenu.splice(
      navigationMenu.findIndex(item => item.key === 'orderLines') + 1,
      0,
      ...wmsItemsMenu,
    );
  }

  const primaryActions = [
    ...(hasPermissions(['odr:orders:write'])
      ? [
          {
            key: 'createOrder',
            text: intl.formatMessage(translations.drawer.primaryAction.options.order),
            onClick: () => history.push('/orders/new'),
          },
          {
            key: 'createPurchase',
            text: intl.formatMessage(translations.drawer.primaryAction.options.purchase),
            onClick: () => history.push('/orders/new?type=purchase'),
          },
        ]
      : []),
    ...(hasPermissions(['odr:invoices:create'])
      ? [
          {
            key: 'createInvoice',
            text: intl.formatMessage(translations.drawer.primaryAction.options.invoice),
            onClick: () => history.push('/invoices/new'),
          },
        ]
      : []),
  ];

  return (
    <AppLayout>
      <Drawer>
        <Dock />
        <DrawerMenu
          title={intl.formatMessage(translations.drawer.appTitle)}
          menu={navigationMenu}
          primaryActionLabel={intl.formatMessage(translations.drawer.primaryAction.label)}
          primaryActions={primaryActions}
        />
      </Drawer>
      <AppWorkspace>
        <Switch>
          <Route component={Attach} path="/attach" />
          <Route component={Providers} path="/providers" />
          <Route component={CreatePricingRule} path="/pricing-rules/new" />
          <Route component={EditPricingRule} path="/pricing-rules/edit/:id" />
          <Route component={PricingRules} path="/pricing-rules" />
          <Route component={TransactionView} path="/transactions/:secret" />
          <Route component={Transactions} path="/transactions" />
          <Route component={CreateInvoice} path="/invoices/new" />
          <Route component={InvoiceView} path="/invoices/:id" />
          <Route component={Invoices} path="/invoices" />
          <Route component={CalculationInvoice} path="/calculations/:calculationID/invoice" />
          <Route component={CalculationView} path="/calculations/:calculationID" />
          <Route component={OrderLines} path="/order-lines" />
          <Route component={CreateOrder} path="/orders/new" />
          <Route component={EditOrder} path="/orders/edit/:id" />
          <Route component={CreateCalculation} path="/orders/:orderID/calculation" />
          <Route component={OrderView} path="/orders/:orderID" />
          <Route component={Orders} path="/orders" />
          <Route component={CreateServiceCost} path="/service-costs/new" />
          <Route component={EditServiceCost} path="/service-costs/edit/:id" />
          <Route component={ServiceCosts} path="/service-costs" />
          <Route component={CouponCategoryView} path="/coupons/:couponCategoryID" />
          <Route component={CouponCategories} path="/coupons" />
          <Redirect to="/orders" />
        </Switch>
      </AppWorkspace>
    </AppLayout>
  );
};

export default Root;
